import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Badge, Chip,
  List, ListItemButton, ListItemText, CircularProgress, Divider,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { api } from '../../api/api';
import socket from '../../socket';

export default function ChatWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);

  // 채팅방 목록 불러오기
  const fetchRooms = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/rooms/all');
      setRooms(data.rooms);
      const total = data.rooms.reduce((sum, r) => sum + (r.unreadCount?.[user?._id] || 0), 0);
      setUnreadTotal(total);
    } catch (_) {}
  }, [user]);

  // 메시지 불러오기
  const fetchMessages = useCallback(async (roomId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chat/messages/${roomId}`);
      setMessages(data.messages);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) fetchRooms();
  }, [open, fetchRooms]);

  // Socket 이벤트 리스너
  useEffect(() => {
    const handleMessage = (msg) => {
      if (activeRoom && msg.room === activeRoom.roomId) {
        setMessages((prev) => [...prev, msg]);
        socket.emit('chat:read', { roomId: activeRoom.roomId });
      }
      fetchRooms();
    };
    const handleTyping = ({ roomId, userName, isTyping }) => {
      if (activeRoom?.roomId === roomId && isTyping) {
        setTypingUser(userName);
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => setTypingUser(null), 2000);
      } else {
        setTypingUser(null);
      }
    };
    const handleNotification = () => { fetchRooms(); };

    socket.on('chat:message', handleMessage);
    socket.on('chat:typing', handleTyping);
    socket.on('chat:notification', handleNotification);

    return () => {
      socket.off('chat:message', handleMessage);
      socket.off('chat:typing', handleTyping);
      socket.off('chat:notification', handleNotification);
    };
  }, [activeRoom, fetchRooms]);

  // 스크롤 to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const enterRoom = (room) => {
    setActiveRoom(room);
    fetchMessages(room.roomId);
    socket.emit('chat:join', { roomId: room.roomId });
  };

  const leaveRoom = () => {
    if (activeRoom) socket.emit('chat:leave', { roomId: activeRoom.roomId });
    setActiveRoom(null);
    setMessages([]);
  };

  const sendMessage = () => {
    if (!input.trim() || !activeRoom) return;
    socket.emit('chat:message', { roomId: activeRoom.roomId, message: input.trim() });
    setInput('');
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (activeRoom) {
      socket.emit('chat:typing', { roomId: activeRoom.roomId, isTyping: true });
    }
  };

  if (!user) return null;

  return (
    <>
      {/* FAB 버튼 */}
      {!open && (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9998,
            width: 56, height: 56, bgcolor: '#551FFF', color: '#fff',
            boxShadow: '0 4px 20px rgba(85,31,255,0.4)',
            '&:hover': { bgcolor: '#4318e0' },
          }}
        >
          <Badge badgeContent={unreadTotal} color="error" max={99}>
            <ChatIcon />
          </Badge>
        </IconButton>
      )}

      {/* 채팅 패널 */}
      {open && (
        <Paper sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 380, height: 520, borderRadius: 3, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
        }}>
          {/* 헤더 */}
          <Box sx={{
            bgcolor: '#551FFF', color: '#fff', px: 2, py: 1.5,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            {activeRoom && (
              <IconButton size="small" sx={{ color: '#fff' }} onClick={leaveRoom}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <ChatIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
              {activeRoom ? activeRoom.storeName || activeRoom.roomId : 'Messages'}
            </Typography>
            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => { leaveRoom(); setOpen(false); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* 콘텐츠 */}
          {!activeRoom ? (
            // 방 목록
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {rooms.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#888', fontSize: 13 }}>
                  채팅방이 없습니다.
                </Box>
              ) : (
                <List disablePadding>
                  {rooms.map((r) => {
                    const myUnread = r.unreadCount?.[user._id] || 0;
                    return (
                      <ListItemButton key={r.roomId} onClick={() => enterRoom(r)}
                        sx={{ borderBottom: '1px solid #f0f0f0' }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                                {r.storeName || r.roomId}
                              </Typography>
                              {myUnread > 0 && (
                                <Chip label={myUnread} size="small" color="error"
                                  sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography sx={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.lastMessage || '메시지 없음'}
                            </Typography>
                          }
                        />
                        {r.lastMessageAt && (
                          <Typography sx={{ fontSize: 10, color: '#bbb', whiteSpace: 'nowrap', ml: 1 }}>
                            {new Date(r.lastMessageAt).toLocaleDateString('ko-KR')}
                          </Typography>
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          ) : (
            // 메시지 뷰
            <>
              <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                {loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: '#888', fontSize: 13 }}>
                    대화를 시작하세요.
                  </Box>
                ) : (
                  messages.map((msg) => {
                    const isMe = String(msg.sender) === String(user._id);
                    return (
                      <Box key={msg._id} sx={{
                        display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start',
                        mb: 1,
                      }}>
                        <Box sx={{
                          maxWidth: '75%', px: 1.5, py: 1,
                          bgcolor: isMe ? '#551FFF' : '#f0f0f5',
                          color: isMe ? '#fff' : '#222',
                          borderRadius: isMe ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        }}>
                          {!isMe && (
                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: isMe ? 'rgba(255,255,255,0.7)' : '#888', mb: 0.3 }}>
                              {msg.senderName}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}>
                            {msg.message}
                          </Typography>
                          <Typography sx={{ fontSize: 9, textAlign: 'right', mt: 0.3, opacity: 0.6 }}>
                            {new Date(msg.createdAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
                {typingUser && (
                  <Typography sx={{ fontSize: 11, color: '#888', px: 1 }}>
                    {typingUser}님이 입력 중...
                  </Typography>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* 입력 영역 */}
              <Box sx={{ p: 1, borderTop: '1px solid #eee', display: 'flex', gap: 0.5 }}>
                <TextField
                  fullWidth size="small" placeholder="메시지 입력..."
                  value={input} onChange={handleInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  sx={{ '& .MuiInputBase-root': { fontSize: 13, borderRadius: 2 } }}
                />
                <IconButton onClick={sendMessage} disabled={!input.trim()}
                  sx={{ color: '#551FFF' }}>
                  <SendIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      )}
    </>
  );
}
